import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useMyFeeDetail } from "@/hooks/useMyFeeDetail";
import { getChildDisplay } from "@/lib/childDisplay";
import { formatMoney, getProgress } from "@/lib/format";
import { cn } from "@/lib/utils";
import { MyFeeStatusBadge } from "./MyFeeStatusBadge";
import { BackHeader, Tag, ProgressBar } from "@/components/kit";

interface MyFeeDetailPageProps {
  feeId: number;
  onBack: () => void;
}

function formatDetailDate(value: string | null | undefined, lang: string) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const locale =
    lang === "ru" ? "ru-RU" : lang === "en" ? "en-US" : "uz-UZ";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-2xl p-3"
      style={{
        background: "var(--c-elevated-1)",
        border: "1px solid var(--c-divider)",
      }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--c-text-faint)" }}>
        {label}
      </p>
      <p className="mt-1 text-sm font-medium" style={{ color: "var(--c-text)" }}>
        {value || "—"}
      </p>
    </div>
  );
}

function StoryBlock({ story }: { story: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 2);
  }, [story]);

  if (!story?.trim()) {
    return (
      <div
        className="rounded-2xl p-4 text-sm"
        style={{
          background: "var(--c-elevated-1)",
          border: "1px solid var(--c-divider)",
          color: "var(--c-text-muted)",
        }}
      >
        {t("myFeesNoDescription")}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: "var(--c-elevated-1)",
        border: "1px solid var(--c-divider)",
      }}
    >
      <p
        ref={textRef}
        className={cn("text-sm leading-relaxed whitespace-pre-line", !expanded && "line-clamp-5")}
        style={{ color: "var(--c-text)" }}
      >
        {story}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-semibold transition-colors"
          style={{ color: "var(--primary)" }}
        >
          {expanded ? t("showLess") : t("showMore")}
        </button>
      )}
    </div>
  );
}

export function MyFeeDetailPage({ feeId, onBack }: MyFeeDetailPageProps) {
  const { t, i18n } = useTranslation();
  const { fee, isLoading, error } = useMyFeeDetail(feeId);

  const display = useMemo(() => (fee ? getChildDisplay(fee) : null), [fee]);
  const progress = fee ? getProgress(fee.collected, fee.amount) : 0;
  const categoryLabel = display
    ? t(`category${display.category.charAt(0).toUpperCase() + display.category.slice(1)}`)
    : "";

  return (
    <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
      <div className="mx-auto max-w-lg">
        <BackHeader title={t("myFeesDetailTitle")} onBack={onBack} />

        <div className="px-4 pb-10 pt-1">
          {isLoading ? (
            <LoadingScreen className="min-h-[45vh]" />
          ) : error || !fee || !display ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm9-3.758a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                  {t("myFeesLoadError")}
                </p>
                {error?.message ? (
                  <p className="mt-1 text-xs" style={{ color: "var(--c-text-muted)" }}>
                    {error.message}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24 }}
              className="overflow-hidden rounded-3xl"
              style={{
                background: "var(--c-elevated-1)",
                border: "1px solid var(--c-divider)",
              }}
            >
              <div className="relative h-72 w-full">
                {display.image ? (
                  <img
                    src={display.image}
                    alt={display.name}
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center" style={{ background: "var(--c-elevated-2)" }}>
                    <span className="text-sm" style={{ color: "var(--c-text-disabled)" }}>
                      {t("noPhoto")}
                    </span>
                  </div>
                )}

                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, var(--app-bg), color-mix(in srgb, var(--app-bg) 22%, transparent), transparent)",
                  }}
                />

                <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                  <Tag tone="neutral">{categoryLabel}</Tag>
                  {fee.typeNeedName ? (
                    <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "var(--c-elevated-2)", color: "var(--c-text)" }}>
                      {fee.typeNeedName}
                    </span>
                  ) : null}
                </div>

                <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
                  <MyFeeStatusBadge status={fee.statusName} statusId={fee.statusId} />
                  {fee.transactionsCount > 0 ? (
                    <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "var(--c-elevated-2)", color: "var(--c-text)" }}>
                      {t("myFeesTransactionsCount")}: {fee.transactionsCount}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="space-y-4 p-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: "var(--c-text)" }}>
                    {display.name}
                  </h2>
                  <p className="mt-1 text-sm" style={{ color: "var(--c-text-muted)" }}>
                    {display.age} {t("yearsOld")} • {display.location}
                  </p>
                  <p className="mt-1 text-sm font-medium" style={{ color: "var(--c-text)" }}>
                    {display.diagnosis || "—"}
                  </p>
                </div>

                <div
                  className="rounded-2xl p-4"
                  style={{
                    background: "var(--c-elevated-1)",
                    border: "1px solid var(--c-divider)",
                  }}
                >
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm" style={{ color: "var(--c-text-muted)" }}>
                    <span>{t("goal")}: {formatMoney(fee.amount)}</span>
                    <span className="font-semibold" style={{ color: "var(--c-text)" }}>{progress}%</span>
                  </div>
                  <ProgressBar value={progress} />
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <span style={{ color: "var(--c-text-muted)" }}>
                      {t("myFeesCollected")}: <strong style={{ color: "var(--c-text)" }}>{formatMoney(fee.collected)}</strong>
                    </span>
                    <span className="text-right" style={{ color: "var(--c-text-muted)" }}>
                      {t("myFeesRemains")}: <strong style={{ color: "var(--c-text)" }}>{formatMoney(fee.remains)}</strong>
                    </span>
                  </div>
                </div>

                {(display.fondName || display.clinicName || fee.typeHelpName) ? (
                  <div className="flex flex-wrap gap-2">
                    {fee.typeHelpName ? (
                      <span className="rounded-full px-3 py-1.5 text-xs font-semibold" style={{ background: "var(--c-brand-tint)", color: "var(--primary)" }}>
                        {fee.typeHelpName}
                      </span>
                    ) : null}
                    {display.fondName ? (
                      <span className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--c-elevated-2)", color: "var(--c-text)" }}>
                        {t("fund")}: {display.fondName}
                      </span>
                    ) : null}
                    {display.clinicName ? (
                      <span className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--c-elevated-2)", color: "var(--c-text)" }}>
                        {t("clinic")}: {display.clinicName}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--c-text-faint)" }}>
                    {t("myFeesDescription")}
                  </p>
                  <StoryBlock story={fee.description} />
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--c-text-faint)" }}>
                    {t("myFeesMetaTitle")}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <DetailField label={t("myFeesStatus")} value={fee.statusName || "—"} />
                    <DetailField
                      label={t("myFeesTransactionsCount")}
                      value={String(fee.transactionsCount ?? 0)}
                    />
                    <DetailField
                      label={t("myFeesCreatedAt")}
                      value={formatDetailDate(fee.createdAt, i18n.language)}
                    />
                    <DetailField
                      label={t("myFeesBirthOn")}
                      value={formatDetailDate(fee.birthOn, i18n.language)}
                    />
                    <DetailField
                      label={t("myFeesTreatmentStartedAt")}
                      value={formatDetailDate(fee.treatmentStartedAt, i18n.language)}
                    />
                    <DetailField
                      label={t("myFeesTypeNeed")}
                      value={fee.typeNeedName || "—"}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
